import { useTranslation } from 'react-i18next'
import EmptyState from '../../../components/ui/EmptyState'
import SectionCard from '../../../components/ui/SectionCard'
import { formatDecimal } from '../../../lib/number'
import { BUCKET_TONE, STATUS_TONE_COLOR_VAR } from '../../actions/types'
import type { ManagerDuration } from '../types'

interface Props {
  rows: ManagerDuration[]
}

/**
 * **"Yönetici Bazlı Ortalama Aksiyon Süresi"** (086 — kullanıcı isteği 2026-08-13 + mockup).
 *
 * <p>Statüsü <b>Tamamlandı</b> olan aksiyonlar için
 * `(Tamamlandı'ya çekildiği tarih − yöneticiye atandığı tarih) / Tamamlandı aksiyon sayısı`. Formül
 * kartın altyazısında da yazar: "18,4 gün" gibi bir sayının neyin ortalaması olduğu grafikten okunamaz.
 *
 * <p><b>Başlangıç noktası kişiye özgüdür</b> (aksiyonun oluşturulma anı değil, o yöneticiye atandığı an) —
 * aynı aksiyon farklı kişilere farklı zamanlarda atanmış olabilir ve kart yönetici bazlıdır.
 *
 * <p><b>Süresi ölçülemeyen kayıtlar hesaba girmez:</b> işlem geçmişinde tamamlanma izi bulunmayan eski
 * aksiyonlar ne paya ne paydaya yazılır (onlara 0 gün saymak ortalamayı olduğundan iyi gösterirdi). Bu
 * yüzden bir yöneticinin buradaki adedi, yandaki çubuk grafiğin "Tamamlandı" segmentinden küçük olabilir.
 *
 * <p>Renk, panonun "Tamamlandı" ana durumuyla <b>aynı</b> kaynaktan gelir (`BUCKET_TONE`) → kart hangi
 * statüyü ölçtüğünü renkle de söyler.
 *
 * <p><b>Düzen (2026-08-17 mockup `cards.png`):</b> liste yerine <b>başlıklı tablo</b> (Ad Soyad · Aksiyon
 * Süresi) — satır yüksekliği küçük, ayıraçlar ince. Gerekçe: kart en fazla <b>10</b> yönetici gösterir
 * (analist kararı: kaydırma yok) ve seyrek satırlı liste düzeni 5 kişilik veride kartı boş gösteriyordu.
 * Sıkı tablo hem 5 hem 10 satırda dengeli durur.
 *
 * <p>Ray {@code role="img"} olarak KALIR: {@code completedCount} (ortalamanın paydası) ekranda yazmaz,
 * yalnız bu özette geçer → aria etiketi kaldırılırsa ekran okuyucu için bilgi kaybı olur.
 */
export default function ManagerDurationCard({ rows }: Readonly<Props>) {
  const { t, i18n } = useTranslation()
  const title = t('home.duration.title')

  if (rows.length === 0) {
    return (
      <SectionCard title={title} hint={t('home.duration.hint')}>
        <EmptyState
          icon="pi pi-clock"
          title={t('home.duration.emptyTitle')}
          description={t('home.duration.emptyDesc')}
        />
      </SectionCard>
    )
  }

  const max = rows.reduce((acc, r) => Math.max(acc, r.averageDays), 0)
  const color = STATUS_TONE_COLOR_VAR[BUCKET_TONE.GROUP_COMPLETED]

  return (
    <SectionCard title={title} hint={t('home.duration.hint')} className="home-duration">
      <p className="home-duration-formula">{t('home.duration.formula')}</p>
      <table className="home-duration-table">
        <thead>
          <tr>
            <th scope="col">{t('home.duration.colName')}</th>
            <th scope="col">{t('home.duration.colDuration')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.employeeId}>
              <td className="home-duration-name" title={r.jobTitle ?? undefined}>
                {r.fullName}
              </td>
              <td>
                <span className="home-duration-cell">
                  <span
                    className="home-duration-track"
                    role="img"
                    aria-label={t('home.duration.summary', {
                      name: r.fullName ?? '',
                      days: formatDecimal(r.averageDays, i18n.language),
                      count: r.completedCount,
                    })}
                  >
                    <span
                      className="home-duration-bar"
                      style={{
                        width: `${max > 0 ? (r.averageDays * 100) / max : 0}%`,
                        background: color,
                      }}
                    />
                  </span>
                  <span className="home-duration-value">
                    {t('home.duration.days', { days: formatDecimal(r.averageDays, i18n.language) })}
                  </span>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="home-brk-note">
        <i className="pi pi-info-circle" aria-hidden /> {t('home.duration.note')}
      </p>
    </SectionCard>
  )
}
